/**
 * BracketPrintView.js
 *
 * Renders a full bracket to a Canvas and provides Print / Save PDF.
 * Triggered from DashboardPage (per-user) and AdminPage (any bracket).
 *
 * Props:
 *   bracket   — bracket doc  { name, userName, picks, playInPicks,
 *                              sleeperTeamId, tiebreaker, ... }
 *   games     — all game docs (array)
 *   teamsById — { [teamId]: { id, name, seed, region, ... } }
 *   wiring    — { regionOrder, finalFour: { semi1, semi2 }, playIn }
 *   branding  — { poolName, logoUrl }
 *   onClose   — callback to close the modal
 *
 * Print: letter landscape (11×8.5in) at 0.25in margins.
 * Canvas: 1027×787px.
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';

// ─── Team/game resolution — mirrors BracketPage.js logic exactly ─────────────

const REGION_KEY   = { South:'s', East:'e', Midwest:'m', West:'w' };
// R64 matchup order: game 1=1v16, 2=8v9, 3=5v12, 4=4v13, 5=6v11, 6=3v14, 7=7v10, 8=2v15
// homeTeam = top seed (lower number), awayTeam = bottom seed

function regionGameIds(region) {
  const k = REGION_KEY[region] || 's';
  return {
    R64: [1,2,3,4,5,6,7,8].map(n => `r64-${k}${n}`),
    R32: [1,2,3,4].map(n => `r32-${k}${n}`),
    R16: [1,2].map(n => `r16-${k}${n}`),
    R8:  [`r8-${k}`],
  };
}

// Resolve a team slot in a game document to { seed, name }
function resolveSlot(slot, gamesById, teamsById, allPicks) {
  if (!slot) return null;
  // Play-in winner slot — only use the user's play-in pick
  if (slot.playInGameId) {
    const pg = gamesById[slot.playInGameId];
    const pick = allPicks[slot.playInGameId];
    if (pick) {
      const t = teamsById[pick] || (pg?.homeTeam?.id === pick ? pg?.homeTeam : pg?.awayTeam);
      return t || { seed: slot.seed, name: 'Play-In' };
    }
    if (pg?.homeTeam && pg?.awayTeam) {
      const t1 = teamsById[pg.homeTeam.id] || pg.homeTeam;
      const t2 = teamsById[pg.awayTeam.id] || pg.awayTeam;
      return { seed: slot.seed, name: `${t1.name} / ${t2.name}` };
    }
    return { seed: slot.seed, name: 'Play-In' };
  }
  // TBD slot
  if (!slot.id || slot.id === 'tbd') return { seed: '', name: 'TBD' };
  // Normal team
  return teamsById[slot.id] || slot;
}

// Resolve which team the user PICKED for a game — picks only, never actual results
function resolveAdvancing(gameId, gamesById, teamsById, allPicks) {
  const g = gamesById[gameId];
  if (!g) return null;
  // Only use the user's pick — this is a picks printout, not a results sheet
  const pid = allPicks[gameId];
  if (pid) {
    return teamsById[pid]
      || (g.homeTeam?.id === pid ? g.homeTeam : g.awayTeam)
      || null;
  }
  return null;
}

// Known multi-word mascot suffixes — strip these before falling back to single-word strip
const MASCOT_SUFFIXES = [
  'Tar Heels','Crimson Tide','Blue Devils','Red Raiders','Horned Frogs',
  'Wolf Pack','Golden Eagles','Mean Green','Green Wave','Red Storm',
  'Blue Jays','Demon Deacons','Fighting Illini','Fighting Irish',
  'Sun Devils','Yellow Jackets','Scarlet Knights','Running Rebels',
  'Cardinal and Gold','Blue Hens','Thundering Herd','Ragin Cajuns',
  'Rainbow Warriors','Mountain Hawks','River Hawks','Road Runners',
  'Flame Throwers','Black Bears','Golden Grizzlies','Golden Flashes',
  'Golden Tornadoes','Silver Hawks','Screaming Eagles','Flying Eagles',
];

function stripMascot(name) {
  if (!name || name === 'TBD') return name;
  const words = name.trim().split(/\s+/);
  // 1-2 word names are already clean school names — never strip them
  if (words.length <= 2) return name;
  const lower = name.toLowerCase();
  // Try known multi-word suffixes first (longest match)
  for (const suffix of MASCOT_SUFFIXES) {
    if (lower.endsWith(suffix.toLowerCase())) {
      const stripped = name.slice(0, name.length - suffix.length).trim();
      if (stripped) return stripped;
    }
  }
  // 3-word names ending in a known school word — keep as-is
  const schoolWords = ['state','marys','mary','dame','johns','john','joseph','xavier','tech','valley','point','canyon','morris','francis'];
  if (words.length === 3 && schoolWords.includes(words[2].toLowerCase())) return name;
  // 3+ word name — strip last word (it's a mascot)
  return words.slice(0, -1).join(' ');
}

function displayName(team) {
  if (!team) return 'TBD';
  const seed = team.seed || '';
  const raw  = team.shortName || team.name || team.abbrev || team.id || 'TBD';
  if (raw === 'TBD' || raw === 'tbd') return 'TBD';
  // Only strip mascot if we don't have a shortName (shortName is already clean)
  const name = team.shortName ? raw : stripMascot(raw);
  return seed ? `${seed} ${name}` : name;
}

// ─── Build region data for canvas ────────────────────────────────────────────
// Returns: { matchups: [[topName,botName]×8], picks: [[0/1×8],[×4],[×2],[×1]] }
function buildRegionData(region, gamesById, teamsById, allPicks) {
  const ids = regionGameIds(region);

  // R64 matchups: home=top, away=bottom
  const matchups = ids.R64.map(gid => {
    const g = gamesById[gid];
    if (!g) return ['TBD', 'TBD'];
    const top = resolveSlot(g.homeTeam, gamesById, teamsById, allPicks);
    const bot = resolveSlot(g.awayTeam, gamesById, teamsById, allPicks);
    return [displayName(top), displayName(bot)];
  });

  // R64 picks: 0=home(top) picked, 1=away(bottom) picked — direct comparison
  const r64picks = ids.R64.map(gid => {
    const g = gamesById[gid];
    if (!g) return 0;
    const pid = allPicks[gid];
    if (!pid) return 0;
    return pid === g.homeTeam?.id ? 0 : 1;
  });

  // For R32+: derive pick index by tracing which team the user picked through prior rounds.
  // For each game, the two candidates come from the picks of the two feeder games.
  // pick=0 means "I pick the same team I picked from the top feeder", pick=1 from bottom feeder.
  // We trace by looking up the user's pick teamId against the derived winner teamIds.

  // Helper: get the teamId the user picked for a given game (traces back through picks)
  function pickedTeamId(gid) {
    return allPicks[gid] || null;
  }

  // R32 picks: game i covers R64 winners of games [i*2] and [i*2+1]
  const r32picks = ids.R32.map((gid, i) => {
    const pid = pickedTeamId(gid);
    if (!pid) return 0;
    // The top candidate is whoever the user picked from R64 game i*2
    const topFeederPick = pickedTeamId(ids.R64[i*2]);
    return pid === topFeederPick ? 0 : 1;
  });

  // S16 picks: game i covers R32 winners of games [i*2] and [i*2+1]
  const s16picks = ids.R16.map((gid, i) => {
    const pid = pickedTeamId(gid);
    if (!pid) return 0;
    // Trace: top candidate is user's R32 pick from game i*2
    const topFeederPick = pickedTeamId(ids.R32[i*2]);
    return pid === topFeederPick ? 0 : 1;
  });

  // E8 pick: single game covering S16 winners of games [0] and [1]
  const e8picks = ids.R8.map((gid) => {
    const pid = pickedTeamId(gid);
    if (!pid) return 0;
    const topFeederPick = pickedTeamId(ids.R16[0]);
    return pid === topFeederPick ? 0 : 1;
  });

  // Build displayed team IDs per column/slot for results mode
  // pickedTeamIds[ri][gi] = [topTeamId, botTeamId]
  const pickedTeamIds = [
    // R64 (ri=0): direct from game homeTeam/awayTeam
    ids.R64.map(gid => {
      const g = gamesById[gid]; if(!g) return ['',''];
      return [String(g.homeTeam?.id||''), String(g.awayTeam?.id||'')];
    }),
    // R32 (ri=1): teams shown = R64 picks
    ids.R32.map((gid,i) => [
      String(allPicks[ids.R64[i*2]]||''),
      String(allPicks[ids.R64[i*2+1]]||''),
    ]),
    // S16 (ri=2): teams shown = R32 picks
    ids.R16.map((gid,i) => [
      String(allPicks[ids.R32[i*2]]||''),
      String(allPicks[ids.R32[i*2+1]]||''),
    ]),
    // E8 (ri=3): teams shown = S16 picks
    ids.R8.map(() => [
      String(allPicks[ids.R16[0]]||''),
      String(allPicks[ids.R16[1]]||''),
    ]),
  ];
  return { matchups, picks: [r64picks, r32picks, s16picks, e8picks], pickedTeamIds };
}

// ─── Build play-in items ──────────────────────────────────────────────────────
function buildPlayinItems(gamesById, allPicks, teamsById, sleeperTeamId) {
  const playinGames = Object.values(gamesById)
    .filter(g => g.round === 'playin')
    .sort((a, b) => (a.id > b.id ? 1 : -1)); // stable order

  // Build play-in winner map for resolving IDs
  const playInWinnerIds = {};
  playinGames.forEach(g => { if (g.winner) playInWinnerIds[g.id] = g.winner; });
  const resolveId = id => (id && playInWinnerIds[id]) ? playInWinnerIds[id] : id;
  // Inject sleeperTeamId into allPicks for the sleeperWon check below
  if (sleeperTeamId) allPicks = { ...allPicks, _sleeperTeamId: sleeperTeamId };

  const regionAbbr = r => {
    if (!r) return '';
    const s = String(r).toLowerCase();
    if (s.includes('mid')) return 'MW';
    if (s.includes('south')) return 'S';
    if (s.includes('east')) return 'E';
    return 'W';
  };

  const hasPlayInPicks = Object.keys(allPicks).some(k => k.startsWith('playin-'));

  return playinGames.slice(0, 4).map(g => {
    const t1 = teamsById[g.homeTeam?.id] || g.homeTeam || {};
    const t2 = teamsById[g.awayTeam?.id] || g.awayTeam || {};
    const pid = allPicks[g.id];
    const pick = pid ? (pid === g.homeTeam?.id ? 0 : 1) : null;
    const region = regionAbbr(g.region || t1.region || '');
    // Determine result for this play-in game
    let result = 'pending';
    if (!hasPlayInPicks) result = g.winner ? 'nopick_final' : 'nopick';
    else if (g.winner && pid) result = pid === g.winner ? 'correct' : 'incorrect';
    else if (g.winner && !pid) result = 'nopick';
    // Check if sleeper won this play-in game
    const winnerTeam = g.winner ? (g.homeTeam?.id === g.winner ? g.homeTeam : g.awayTeam) : null;
    const winnerIsLeft  = winnerTeam?.id === (teamsById[g.homeTeam?.id] || g.homeTeam)?.id;
    const sleeperWon = !!(g.winner && sleeperTeamId && 
      resolveId(g.winner) === resolveId(sleeperTeamId));
    return {
      left:   displayName(t1),
      right:  displayName(t2),
      region,
      pick,
      result: result === 'nopick_final' ? 'incorrect' : result,
      nopick: !hasPlayInPicks,
      sleeperWon,
      sleeperEligible: !!pid, // only show star if they have a pick for this game
      winnerSide: g.winner ? (g.homeTeam?.id === g.winner ? 0 : 1) : null,
    };
  });
}

// ─── Build per-game result status for results mode ───────────────────────────
function buildResultMap(bracket, games, teamsById, wiring) {
  const gamesById = {};
  games.forEach(g => { gamesById[g.id] = g; });

  const allPicks = { ...(bracket?.picks || {}), ...(bracket?.playInPicks || {}) };
  const hasPlayInPicks = Object.keys(bracket?.playInPicks || {}).length > 0;
  const sleeperTeamId = bracket?.sleeperTeamId;

  // ── Step 1: Build the set of ALL eliminated team IDs ─────────────────────
  // For every completed game, the loser is eliminated.
  // Play-in games use text slug IDs; R64+ games use ESPN numeric IDs.
  // We collect both so we can match against any pick format.
  const eliminatedIds = new Set();

  games.forEach(g => {
    if (!g.winner) return;
    const homeId = String(g.homeTeam?.id || '');
    const awayId = String(g.awayTeam?.id || '');
    if (!homeId || !awayId || homeId === 'tbd' || awayId === 'tbd') return;
    const loserId = String(g.winner) === homeId ? awayId : homeId;
    if (loserId) eliminatedIds.add(loserId);
  });

  // Also add loser names (normalized) as an additional lookup key,
  // in case picks use a different ID format than the game doc
  const eliminatedNames = new Set();
  games.forEach(g => {
    if (!g.winner) return;
    const homeId = String(g.homeTeam?.id || '');
    const awayId = String(g.awayTeam?.id || '');
    const loserId = String(g.winner) === homeId ? awayId : homeId;
    const loserTeam = loserId === homeId ? g.homeTeam : g.awayTeam;
    if (loserTeam?.name) {
      eliminatedNames.add(loserTeam.name.toLowerCase().replace(/[^a-z0-9]/g, ''));
    }
  });

  const isEliminated = (pid) => {
    if (!pid) return false;
    if (eliminatedIds.has(String(pid))) return true;
    // Try name match via teamsById
    const team = teamsById[pid];
    if (team?.name) {
      return eliminatedNames.has(team.name.toLowerCase().replace(/[^a-z0-9]/g, ''));
    }
    return false;
  };



  // ── Step 2: Build play-in winner resolution map ───────────────────────────
  const playInWinners = {};
  games.forEach(g => { if (g.round === 'playin' && g.winner) playInWinners[g.id] = g.winner; });
  const resolveId = id => (id && playInWinners[id]) ? playInWinners[id] : id;

  // ── Step 3: Build result map for every game ───────────────────────────────
  const resultMap = {};

  games.forEach(g => {
    const gid = g.id;
    const isPlayIn = g.round === 'playin';
    const sleeperWon = !!(g.winner && sleeperTeamId && resolveId(g.winner) === resolveId(sleeperTeamId));

    if (isPlayIn && !hasPlayInPicks) {
      resultMap[gid] = { status: g.winner ? 'nopick' : 'pending', sleeperWon };
      return;
    }

    const pid = allPicks[gid];
    if (!g.winner) {
      // Game not played yet — check if picked team is already eliminated
      if (pid && isEliminated(pid)) {
        resultMap[gid] = { status: 'eliminated', sleeperWon: false, winner: null };
      } else {
        resultMap[gid] = { status: pid ? 'pending' : 'nopick', sleeperWon: false, winner: null };
      }
      return;
    }

    // Game is final
    if (!pid) { resultMap[gid] = { status: 'nopick', sleeperWon, winner: g.winner }; return; }
    const resolvedPick = resolveId(pid);
    const correct = String(resolvedPick) === String(resolveId(g.winner));
    resultMap[gid] = { status: correct ? 'correct' : 'incorrect', sleeperWon, winner: g.winner, pickedId: pid };
  });

  return { resultMap, eliminatedIds, gamesById };
}

// ─── Translate Firestore bracket props → canvas DATA object ──────────────────
function translateData({ bracket, games, teamsById, wiring, branding, espnNameCache = {}, scoringConfig = {}, playInRoundName = 'First Four' }) {
  const gamesById = {};
  (games || []).forEach(g => { gamesById[g.id] = g; });

  const allPicks = { ...(bracket?.picks || {}), ...(bracket?.playInPicks || {}) };

  // Normalize region order to proper-case
  const normalizeRegion = r => {
    const s = String(r);
    if (/mid/i.test(s)) return 'Midwest';
    if (/south/i.test(s)) return 'South';
    if (/east/i.test(s)) return 'East';
    if (/west/i.test(s)) return 'West';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };
  const regionOrder = (wiring?.regionOrder || ['South','East','Midwest','West'])
    .map(normalizeRegion);

  // Build region matchups and picks
  const regions = {}, picks = {}, pickedTeamIds = {};
  regionOrder.forEach(r => {
    const { matchups, picks: rPicks, pickedTeamIds: rPTI } = buildRegionData(r, gamesById, teamsById, allPicks);
    regions[r] = matchups.map(([a, b]) => [a, '', b, '']);
    picks[r]   = rPicks;
    pickedTeamIds[r] = rPTI;
  });

  // Final Four picks — use resolveAdvancing which mirrors BracketPage exactly
  const getSemiFinal = (gameId) => {
    const t = resolveAdvancing(gameId, gamesById, teamsById, allPicks);
    return t ? displayName(t) : 'TBD';
  };

  // Sleeper — look up by ID, fall back to espnNameCache for numeric IDs
  const sleeperTeam = teamsById[bracket?.sleeperTeamId];
  const sleeper = sleeperTeam
    ? displayName(sleeperTeam)
    : (espnNameCache?.[bracket?.sleeperTeamId] || bracket?.sleeperTeamId || '—');

  return {
    poolName:    branding?.poolName || 'March Madness',
    logoUrl:     branding?.logoUrl  || null,
    bracketName: bracket?.name      || '',
    userName:    bracket?.userName  || '',
    tiebreaker:  bracket?.tiebreaker != null ? bracket.tiebreaker : null,
    sleeper,
    sleeperTeamId: bracket?.sleeperTeamId || null,
    allPicks,
    semi1:  getSemiFinal('r4-1'),
    semi2:  getSemiFinal('r4-2'),
    champ:  getSemiFinal('final-1'),
    regionOrder,
    regions,
    picks,
    pickedTeamIds,
    playin: buildPlayinItems(gamesById, allPicks, teamsById, bracket?.sleeperTeamId),
    playInBonus: scoringConfig?.playInBonus ?? 1,
    playInRoundName: playInRoundName || 'First Four',
  };
}

// ─── Canvas drawing engine ────────────────────────────────────────────────────
function drawBracket(canvas, data, logoImg, mode, resultMap, eliminatedIds, resultGamesById) {
  const ctx = canvas.getContext('2d');
  const DPR = 2;  // render at 2x for crisp print quality
  // Logical dimensions (all drawing code uses these)
  const W = 1027, H = 762;
  // Scale context so all drawing commands work in logical pixels
  ctx.setTransform(DPR, 0, 0, DPR, 0, 0);

  const NAVY='#1a3a5c', BLUE='#4B8BBE', BLK='#111', DGRAY='#333';
  const GRAY='#666', LGRAY='#aaa', WHITE='#fff';
  const GREEN='#1a7a3a', RED='#b02020', GOLD='#b8860b';
  const isResults = mode === 'results';
  const F="'Barlow Condensed',sans-serif", FB="'Bebas Neue',sans-serif";

  const tx = (x,y,s,sz,col,align,bold,font) => {
    if (s===null||s===undefined||s==='') return;
    ctx.save();
    ctx.font=(bold?'700 ':'400 ')+sz+'px '+(font||F);
    ctx.fillStyle=col||BLK; ctx.textAlign=align||'left'; ctx.textBaseline='alphabetic';
    ctx.fillText(String(s),x,y); ctx.restore();
  };
  const ln = (x1,y1,x2,y2,col,lw) => {
    ctx.save(); ctx.strokeStyle=col||DGRAY; ctx.lineWidth=lw||0.7;
    ctx.beginPath(); ctx.moveTo(x1,y1); ctx.lineTo(x2,y2); ctx.stroke(); ctx.restore();
  };
  const bx = (x,y,w,h,fill,stroke,slw) => {
    ctx.save();
    if(fill)   {ctx.fillStyle=fill;   ctx.fillRect(x,y,w,h);}
    if(stroke) {ctx.strokeStyle=stroke; ctx.lineWidth=slw||0.7; ctx.strokeRect(x,y,w,h);}
    ctx.restore();
  };
  const clip = fn => { ctx.save(); fn(); ctx.restore(); };
  const shr  = s   => s ? String(s).replace(/\s*\(\d+[-\d]+\)/g,'').trim() : '';

  // Geometry
  const HDR_H=28, RHDR_H=13, PI_H=24;
  const TOP=HDR_H+RHDR_H, BH=H-PI_H-TOP, LH=BH/32, RH=LH*16;
  const CW=196, FF_W=70, SW=(W-CW)/2, COL_W=(SW-FF_W)/4;
  const L_R1=0, L_R2=COL_W, L_R3=COL_W*2, L_R4=COL_W*3, L_FF=COL_W*4;
  const CX1=SW, CX2=SW+CW;
  const R_FF=CX2, R_R4=CX2+FF_W, R_R3=CX2+FF_W+COL_W, R_R2=CX2+FF_W+COL_W*2, R_R1=CX2+FF_W+COL_W*3;
  const MX=CX1+CW/2;

  function buildWinners(teams, picks) {
    const w1=teams.map(([a,,b,,],i)=>picks[0][i]===1?[b,'']:[a,'']);
    const w2=Array.from({length:4},(_,i)=>picks[1][i]===1?w1[i*2+1]:w1[i*2]);
    const w3=Array.from({length:2},(_,i)=>picks[2][i]===1?w2[i*2+1]:w2[i*2]);
    return {w1,w2,w3};
  }

  function drawRegion(teams, picks, regionOY, side, label, regionGameIds_, pickedTeamIds_) {
    const left=side==='L', {w1,w2,w3}=buildWinners(teams,picks), oy=TOP+regionOY;
    const rounds=[
      {n:8,span:2, wins:null,pk:picks[0]},
      {n:4,span:4, wins:w1,  pk:picks[1]},
      {n:2,span:8, wins:w2,  pk:picks[2]},
      {n:1,span:16,wins:w3,  pk:picks[3]},
    ];
    rounds.forEach(({n,span,wins,pk},ri)=>{
      const spanH=span*LH;
      const colX=ri===0?(left?L_R1:R_R1):ri===1?(left?L_R2:R_R2):ri===2?(left?L_R3:R_R3):(left?L_R4:R_R4);
      const lx1=colX, lx2=colX+COL_W-1, innerX=left?lx2:lx1;
      const rids = regionGameIds_ ? (ri===0?regionGameIds_.R64:ri===1?regionGameIds_.R32:ri===2?regionGameIds_.R16:regionGameIds_.R8) : [];
      for(let gi=0;gi<n;gi++){
        const gTop=oy+gi*spanH, yA=gTop+spanH*0.25, yB=gTop+spanH*0.75, yM=(yA+yB)/2;
        const pA=pk[gi]===0, pB=pk[gi]===1;
        let nA='',nB='';
        if(ri===0){nA=shr(teams[gi][0]);nB=shr(teams[gi][2]);}
        else{nA=wins[gi*2]?shr(wins[gi*2][0]):'';nB=wins[gi*2+1]?shr(wins[gi*2+1][0]):'';}        // All team names BLACK — presence on line shows the pick
        ln(lx1,yA,lx2,yA,pA?BLK:LGRAY,pA?0.9:0.7);
        ln(lx1,yB,lx2,yB,pB?BLK:LGRAY,pB?0.9:0.7);
        const tnX=left?lx1+1:lx2-1, tnA=left?'left':'right';
        // In Results mode (R32+), color team names to match the ✓/✗ symbol on that line
        const getNameColor = (teamId, lineY) => {
          if(!isResults || ri===0 || !teamId) return BLK;
          const feederRids0 = ri===1?(regionGameIds_?.R64||[]) : ri===2?(regionGameIds_?.R32||[]) : (regionGameIds_?.R16||[]);
          const slot0 = (lineY===yA) ? 0 : 1;
          const feederGid0 = ri===3 ? feederRids0[slot0] : feederRids0[gi*2+slot0];
          const feederGame0 = feederGid0 ? resultGamesById?.[feederGid0] : null;
          if(feederGame0?.winner) return String(feederGame0.winner)===String(teamId) ? GREEN : RED;
          if(eliminatedIds?.has(String(teamId))) return RED;
          return BLK;
        };
        const ids0 = (isResults && ri>0) ? (pickedTeamIds_?.[ri]?.[gi] || ['','']) : ['',''];
        const cA = getNameColor(ids0[0], yA);
        const cB = getNameColor(ids0[1], yB);
        clip(()=>{ctx.rect(lx1,yA-LH+2,lx2-lx1,LH-1);ctx.clip();tx(tnX,yA-2,nA,10,cA,tnA,true);});
        clip(()=>{ctx.rect(lx1,yB-LH+2,lx2-lx1,LH-1);ctx.clip();tx(tnX,yB-2,nB,10,cB,tnA,true);});
        // Results mode — skip R64, draw ✓/✗ on both team lines in R32+
        // Logic: for each game slot, look up what team the user picked.
        // The picked team is shown on pA line (top) or pB line (bottom).
        // Check if that team actually won the game (resultGamesById[gid].winner === pick).
        // The other displayed team gets the opposite symbol.
        // Results: ✓/✗ on every team line in R32+
        // Uses pickedTeamIds_[ri][gi] = [topId, botId] — the actual team shown on each line
        if(isResults && ri > 0){
          const sx = left?lx2-1:lx1+1, sa = left?'right':'left';
          const sz = 14;
          const ids2 = pickedTeamIds_?.[ri]?.[gi] || ['',''];
          const topId = ids2[0], botId = ids2[1];
          const symForTeam = (teamId, lineY) => {
            if(!teamId) return;
            // Find the feeder game — the game this team just played to get here
            // For ri=1(R32): feeder = R64 games; ri=2(S16): feeder = R32; ri=3(E8): feeder = S16
            const feederRids = ri===1?(regionGameIds_?.R64||[]) : ri===2?(regionGameIds_?.R32||[]) : (regionGameIds_?.R16||[]);
            // top slot came from feederRids[gi*2], bot slot from feederRids[gi*2+1]
            const slot = (lineY===yA) ? 0 : 1;
            const feederGid = ri===3 ? feederRids[slot] : feederRids[gi*2+slot];
            const feederGame = feederGid ? resultGamesById?.[feederGid] : null;
            if(feederGame?.winner){
              const won = String(feederGame.winner)===String(teamId);
              tx(sx, lineY-1, won?'✓':'✗', sz, won?GREEN:RED, sa, true);
            } else if(eliminatedIds?.has(String(teamId))){
              tx(sx, lineY-1, '✗', sz, RED, sa, true);
            }
          };
          if(topId && nA) symForTeam(topId, yA);
          if(botId && nB) symForTeam(botId, yB);
          // Sleeper star — only show when the sleeper team WON the feeder game for that line
          const sleeperTeamId = data.sleeperTeamId || '';
          if(sleeperTeamId){
            const checkSleeperStar = (teamId, lineY, slot) => {
              if(!teamId || String(teamId)!==String(sleeperTeamId)) return;
              const feederRids2 = ri===1?(regionGameIds_?.R64||[]) : ri===2?(regionGameIds_?.R32||[]) : (regionGameIds_?.R16||[]);
              const feederGid2 = ri===3 ? feederRids2[slot] : feederRids2[gi*2+slot];
              const feederGame2 = feederGid2 ? resultGamesById?.[feederGid2] : null;
              if(feederGame2?.winner && String(feederGame2.winner)===String(teamId)){
                tx(left?sx-sz:sx+sz, lineY-1, '★', sz, GOLD, sa, true);
              }
            };
            if(topId && nA) checkSleeperStar(topId, yA, 0);
            if(botId && nB) checkSleeperStar(botId, yB, 1);
          }
        }
        ln(innerX,yA,innerX,yB,LGRAY,0.7);
        const nextX=ri<3?(left?(ri===0?L_R2:ri===1?L_R3:L_R4):(ri===0?R_R2+COL_W:ri===1?R_R3+COL_W:R_R4+COL_W)):(left?CX1:CX2);
        ln(innerX,yM,nextX,yM,LGRAY,0.7);
      }
    });
    // Final Four: show the E8 pick name + results symbol
    {
      const ffX = left ? L_FF : R_FF;
      const ffY = oy + RH/2;
      const champName = picks[3][0]===0&&w3[0]?shr(w3[0][0]) : picks[3][0]===1&&w3[1]?shr(w3[1][0]) : '';
      // Get the E8 pick ID directly from allPicks
      const e8gid = regionGameIds_?.R8?.[0];
      const champId = e8gid ? data.allPicks?.[e8gid] : null;
      if(champName){
        // Color FF name green/red in Results mode based on E8 result
        const ffNameColor = (() => {
          if(!isResults || !champId || !e8gid) return BLK;
          const e8res0 = resultGamesById?.[e8gid];
          if(e8res0?.winner) return String(e8res0.winner)===String(champId) ? GREEN : RED;
          if(eliminatedIds?.has(String(champId))) return RED;
          return BLK;
        })();
        clip(()=>{ctx.rect(ffX+1,ffY-LH+2,FF_W-2,LH-1);ctx.clip();
          tx(left?ffX+3:ffX+FF_W-3, ffY-2, champName, 10, ffNameColor, left?'left':'right', true);});
        // Results symbol
        if(isResults && champId && e8gid){
          const e8res = resultGamesById?.[e8gid];
          const sx = left?ffX+FF_W-1:ffX+1, sa = left?'right':'left';
          if(e8res?.winner){
            const won = String(e8res.winner)===String(champId);
            tx(sx, ffY-1, won?'✓':'✗', 14, won?GREEN:RED, sa, true);
          } else if(eliminatedIds?.has(String(champId))){
            tx(sx, ffY-1, '✗', 14, RED, sa, true);
          }
          // Sleeper star — only when e8 game has a result AND sleeper won it
          if(data.sleeperTeamId && String(champId)===String(data.sleeperTeamId) && e8res?.winner && String(e8res.winner)===String(champId))
            tx(left?sx-14:sx+14, ffY-1, '★', 14, GOLD, sa, true);
        }
      }
    }

    ctx.save();
    ctx.font='700 14px '+FB; ctx.fillStyle=BLK;
    ctx.textAlign=left?'left':'right'; ctx.textBaseline='middle';
    ctx.fillText(label,left?L_R3+COL_W+8:R_R3-8,TOP+regionOY+RH/2);
    ctx.restore();
  }

  function drawCenter() {
    const cx=MX;
    // Fixed pixel positions (measured from approved reference)
    const bnY=87,nameY=103,logoR=52,logoY=237;
    const semiY=347,sbh=22,sbw=68,gap=6;
    const s1x=cx-sbw-gap/2, s2x=cx+gap/2;
    const natY=453,champY=465,cbh=30,cbw=110;
    const tbY=564,tbValY=585,slY=620,slValY=635;

    // BRACKET / NAME
    tx(cx,  bnY,  'BRACKET:',11,BLUE,'right',true,FB);
    tx(cx+4,bnY,  data.bracketName,12,BLK,'left',true);
    tx(cx,  nameY,'NAME:',   11,BLUE,'right',true,FB);
    tx(cx+4,nameY,data.userName,   12,BLK,'left',false);

    // Logo — drawn at natural aspect ratio, no circle crop, centered at logoY
    if(logoImg){
      // Fit image within a 120×110 box, preserving aspect ratio
      const maxW=120, maxH=110;
      const iw=logoImg.naturalWidth||logoImg.width||120;
      const ih=logoImg.naturalHeight||logoImg.height||110;
      const scale=Math.min(maxW/iw, maxH/ih);
      const dw=iw*scale, dh=ih*scale;
      ctx.drawImage(logoImg, cx-dw/2, logoY-dh/2, dw, dh);
    } else {
      // Fallback: navy rounded rect with pool name
      ctx.save(); ctx.beginPath();
      const fr=8, fx=cx-60, fy=logoY-40, fw=120, fh=80;
      ctx.moveTo(fx+fr,fy); ctx.lineTo(fx+fw-fr,fy);
      ctx.arcTo(fx+fw,fy,fx+fw,fy+fr,fr); ctx.lineTo(fx+fw,fy+fh-fr);
      ctx.arcTo(fx+fw,fy+fh,fx+fw-fr,fy+fh,fr); ctx.lineTo(fx+fr,fy+fh);
      ctx.arcTo(fx,fy+fh,fx,fy+fh-fr,fr); ctx.lineTo(fx,fy+fr);
      ctx.arcTo(fx,fy,fx+fr,fy,fr); ctx.closePath();
      ctx.fillStyle=NAVY; ctx.fill();
      const words=(data.poolName||'').split(' ');
      ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillStyle=WHITE;
      ctx.font='700 16px '+FB; ctx.fillText(words.slice(0,2).join(' '),cx,logoY-8);
      if(words[2]){ctx.font='700 14px '+FB;ctx.fillText(words.slice(2).join(' '),cx,logoY+11);}
      ctx.restore();
    }

    // Semis — measure text width and size boxes to fit, using CW/2 as max
    const maxSemiW = CW/2 - 8;
    ctx.font = '700 8.5px ' + F;
    const s1w = Math.min(maxSemiW, Math.max(56, ctx.measureText(data.semi1).width + 12));
    const s2w = Math.min(maxSemiW, Math.max(56, ctx.measureText(data.semi2).width + 12));
    const s1x2 = cx - gap/2 - s1w;
    const s2x2 = cx + gap/2;

    bx(s1x2,semiY,s1w,sbh,BLUE);
    clip(()=>{ctx.rect(s1x2,semiY,s1w,sbh);ctx.clip();tx(s1x2+s1w/2,semiY+16,data.semi1,8.5,WHITE,'center',true);});
    tx(s1x2+s1w/2,semiY+sbh+8,'SEMIFINALS',6,BLUE,'center',true,FB);
    if(isResults){
      const r41 = resultGamesById?.['r4-1']; const p41 = data.allPicks?.['r4-1'];
      if(r41?.winner && p41){ const w41=String(r41.winner)===String(p41); tx(s1x2-2,semiY+16,w41?'✓':'✗',14,w41?GREEN:RED,'right',true); }
    }

    bx(s2x2,semiY,s2w,sbh,BLUE);
    clip(()=>{ctx.rect(s2x2,semiY,s2w,sbh);ctx.clip();tx(s2x2+s2w/2,semiY+16,data.semi2,8.5,WHITE,'center',true);});
    tx(s2x2+s2w/2,semiY+sbh+8,'SEMIFINALS',6,BLUE,'center',true,FB);
    if(isResults){
      const r42 = resultGamesById?.['r4-2']; const p42 = data.allPicks?.['r4-2'];
      if(r42?.winner && p42){ const w42=String(r42.winner)===String(p42); tx(s2x2+s2w+2,semiY+16,w42?'✓':'✗',14,w42?GREEN:RED,'left',true); }
    }

    // Update FF lines to use dynamic semi box positions
    const _s1x = s1x2, _s2x = s2x2, _s1w = s1w, _s2w = s2w;

    // National Championship — size box to fit champion name
    tx(cx,natY,'NATIONAL CHAMPIONSHIP',11,BLK,'center',true,FB);
    ctx.font = '700 15px ' + F;
    const champTextW = ctx.measureText(data.champ).width;
    const cbwDyn = Math.min(CW - 20, Math.max(100, champTextW + 24));
    bx(cx-cbwDyn/2,champY,cbwDyn,cbh,null,NAVY,1.5);
    clip(()=>{ctx.rect(cx-cbwDyn/2,champY,cbwDyn,cbh);ctx.clip();tx(cx,champY+22,data.champ,15,NAVY,'center',true);});
    if(isResults){
      const rf1 = resultGamesById?.['final-1']; const pf1 = data.allPicks?.['final-1'];
      if(rf1?.winner && pf1){ const wf1=String(rf1.winner)===String(pf1); tx(cx+cbwDyn/2+4,champY+22,wf1?'✓':'✗',14,wf1?GREEN:RED,'left',true); }
    }

    // Tiebreaker
    tx(cx,tbY,   'TIEBREAKER',13,BLK,'center',true,FB);
    tx(cx,tbValY,data.tiebreaker!=null?String(data.tiebreaker):'—',22,BLK,'center',true,FB);

    // Sleeper
    tx(cx,slY,   'SLEEPER',  13,BLK,'center',true,FB);
    tx(cx,slValY,data.sleeper||'—',11,BLK,'center',true);

    // First Four label above footer


    // Connector lines from FF column edges to semi boxes
    const e8yTop=TOP+RH*0.5, e8yBot=TOP+RH*1.5, semiMid=semiY+sbh/2;
    const ffMidY = (e8yTop+e8yBot)/2;
    // Left: vertical bracket at CX1, then horizontal to semi1
    ln(CX1,e8yTop,CX1,e8yBot,LGRAY);
    ln(CX1,ffMidY,s1x2,semiMid,LGRAY);
    // Right: vertical bracket at CX2, then horizontal to semi2
    ln(CX2,e8yTop,CX2,e8yBot,LGRAY);
    ln(CX2,ffMidY,s2x2+s2w,semiMid,LGRAY);
  }

  function drawHeader() {
    bx(0,0,W,HDR_H,NAVY);
    tx(W/2,HDR_H-8,(data.poolName||'MARCH MADNESS').toUpperCase(),18,WHITE,'center',true,FB);
  }

  function drawRoundHeaders() {
    bx(0,HDR_H,W,RHDR_H,'#eef3f8');
    ln(0,HDR_H+RHDR_H,W,HDR_H+RHDR_H,LGRAY,0.5);
    const hy=HDR_H+RHDR_H-3;
    const LN=['FIRST ROUND','SECOND ROUND','SWEET 16','ELITE 8'];
    const RN=['ELITE 8','SWEET 16','SECOND ROUND','FIRST ROUND'];
    LN.forEach((n,i)=>tx([L_R1,L_R2,L_R3,L_R4][i]+COL_W/2,hy,n,6.5,DGRAY,'center',true,FB));
    RN.forEach((n,i)=>tx([R_R4,R_R3,R_R2,R_R1][i]+COL_W/2,hy,n,6.5,DGRAY,'center',true,FB));
    tx(L_FF+FF_W/2,hy,'FINAL FOUR',6.5,DGRAY,'center',true,FB);
    tx(R_FF+FF_W/2,hy,'FINAL FOUR',6.5,DGRAY,'center',true,FB);
    tx(CX1+CW*0.25,hy,'NATIONAL SEMIFINALS',6,DGRAY,'center',true,FB);
    tx(CX1+CW*0.75,hy,'NATIONAL SEMIFINALS',6,DGRAY,'center',true,FB);
  }

  function drawPlayin() {
    // Full-width blue bar at bottom — matches reference image style
    const barY = H - PI_H;
    bx(0, barY, W, PI_H, BLUE);

    // Play-In round name label is drawn after drawPlayin() in the render sequence
    // so it appears above the bar without being covered by it

    const items = data.playin || [];
    if (!items.length) return;

    // 4 game boxes evenly spaced across the full width
    // Each box: region label on left, two white team-name boxes stacked, score on right
    const boxW  = 148;   // white box width
    const boxH  = 10;    // each team row height
    const pad   = 2;     // gap between the two rows
    const totalW = boxW * 4 + 24 * 3;  // 4 boxes + 3 gaps of 24px
    const startX = (W - totalW) / 2;
    const rowY   = barY + (PI_H - boxH*2 - pad) / 2;  // vertically centered in bar

    items.forEach((g, i) => {
      const bxX = startX + i * (boxW + 24);
      const yA  = rowY;
      const yB  = rowY + boxH + pad;

      // Region label to the left of the box
      tx(bxX - 3, yA + boxH - 1, g.region, 7.5, WHITE, 'right', true, FB);

      // Two white team boxes stacked
      bx(bxX, yA, boxW, boxH, WHITE);
      bx(bxX, yB, boxW, boxH, WHITE);

      // Team names — color based on mode and result
      // nopick means user had no play-in picks (missed deadline) — always DGRAY, no blue
      const getPlayinColor = (isPickedTeam, gameResult) => {
        if (g.nopick) return DGRAY;  // missed deadline — no blue at all
        if (!isPickedTeam) return DGRAY;
        if (!isResults) return BLUE;
        if (gameResult === 'correct') return GREEN;
        if (gameResult === 'incorrect') return RED;
        return BLUE;
      };
      const c1 = getPlayinColor(g.pick===0, g.result);
      const c2 = getPlayinColor(g.pick===1, g.result);
      // In results mode, determine what symbol(s) to show
      // - Picked team: ✓ correct, ✗ wrong
      // - Nopick (missed deadline): ✗ on the winner's row
      // - Sleeper won: ★ gold on that team's row (regardless of pick)
      const drawPlayinSymbols = (isTop) => {
        if (!isResults) return;
        const rowY_ = isTop ? yA : yB;
        const isPicked = isTop ? g.pick===0 : g.pick===1;
        const isWinner = isTop ? g.winnerSide===0 : g.winnerSide===1;
        const isSleeper = isTop ? 
          (g.sleeperWon && g.sleeperEligible && g.winnerSide===0) : 
          (g.sleeperWon && g.sleeperEligible && g.winnerSide===1);

        let symbol = null, symColor = null;
        if (!g.nopick && isPicked) {
          symbol = g.result==='correct' ? '✓' : g.result==='incorrect' ? '✗' : null;
          symColor = g.result==='correct' ? GREEN : RED;
        } else if (g.nopick && isWinner && g.result!=='pending') {
          // Missed deadline — show ✗ on winner's row
          symbol = '✗'; symColor = RED;
        }

        // Draw result symbol
        if (symbol) tx(bxX+boxW-2, rowY_+boxH-2, symbol, 7, symColor, 'right', true);
        // Draw sleeper star
        if (isSleeper) {
          const starX = symbol ? bxX+boxW-12 : bxX+boxW-2;
          tx(starX, rowY_+boxH-2, '★', 7, GOLD, 'right', true);
        }
      };

      clip(()=>{ ctx.rect(bxX+2,yA,boxW-4,boxH); ctx.clip();
        tx(bxX+3, yA+boxH-2, g.left, 7.5, c1, 'left', !g.nopick && g.pick===0);
        drawPlayinSymbols(true);
      });
      clip(()=>{ ctx.rect(bxX+2,yB,boxW-4,boxH); ctx.clip();
        tx(bxX+3, yB+boxH-2, g.right, 7.5, c2, 'left', !g.nopick && g.pick===1);
        drawPlayinSymbols(false);
      });
    });

    // Trophy banner — show in Results mode when all 4 play-in picks are correct
    if (isResults && items.length === 4 && items.every(g => g.result === 'correct')) {
      const bonusPts = data.playInBonus ?? 1;
      const bannerText = `🏆  Perfect ${data.playInRoundName||'Play-In'} Bonus = +${bonusPts}`;
      // Draw on the right side of the bar so it doesn't collide with the game boxes
      tx(W - 8, barY + PI_H - 5, bannerText, 9, WHITE, 'right', true, FB);
    }
  }

  // ── Render ──
  bx(0,0,W,H,WHITE);
  drawHeader();
  drawRoundHeaders();
  // regionOrder: [1=top-left, 2=top-right, 3=bottom-right, 4=bottom-left]
  const [r0,r1,r2,r3]=data.regionOrder;
  drawRegion(data.regions[r0],data.picks[r0],0, 'L',r0.toUpperCase(),regionGameIds(r0),data.pickedTeamIds?.[r0]);  // 1: top-left
  drawRegion(data.regions[r3],data.picks[r3],RH,'L',r3.toUpperCase(),regionGameIds(r3),data.pickedTeamIds?.[r3]);  // 4: bottom-left
  drawRegion(data.regions[r1],data.picks[r1],0, 'R',r1.toUpperCase(),regionGameIds(r1),data.pickedTeamIds?.[r1]);  // 2: top-right
  drawRegion(data.regions[r2],data.picks[r2],RH,'R',r2.toUpperCase(),regionGameIds(r2),data.pickedTeamIds?.[r2]);  // 3: bottom-right
  drawCenter();
  drawPlayin();
  // Draw play-in round name label last so it sits cleanly above the blue bar
  tx(W/2, H-PI_H-4, (data.playInRoundName||'First Four').toUpperCase(), 10, DGRAY, 'center', true, FB);
}

// ─── React component ──────────────────────────────────────────────────────────
export default function BracketPrintView({ bracket, games, teamsById, wiring, branding, onClose, espnNameCache = {}, scoringConfig = {}, playInRoundName = 'First Four' }) {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState('picks'); // 'picks' | 'results'

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try {
    const data = translateData({ bracket, games, teamsById, wiring, branding, espnNameCache, scoringConfig, playInRoundName });
    const resultData = mode === 'results' ? buildResultMap(bracket, games, teamsById, wiring) : null;
    const resultMap = resultData?.resultMap ?? null;
    const eliminatedIds = resultData?.eliminatedIds ?? new Set();
    const resultGamesById = resultData?.gamesById ?? {};
    console.log('[DRAW] data ready, regions=', data.regionOrder, 'playin=', data.playin?.length);

    if (data.logoUrl) {
      // Fetch logo as blob to avoid Firebase Storage CORS canvas-taint issues.
      // Using fetch → blob → objectURL keeps the canvas clean for toDataURL().
      fetch(data.logoUrl, { mode: 'cors' })
        .then(r => {
          if (!r.ok) throw new Error(`Logo fetch ${r.status}`);
          return r.blob();
        })
        .then(blob => {
          const objUrl = URL.createObjectURL(blob);
          const img = new Image();
          img.onload = () => {
            drawBracket(canvas, data, img, mode, resultMap, eliminatedIds, resultGamesById);
            URL.revokeObjectURL(objUrl);
          };
          img.onerror = () => {
            URL.revokeObjectURL(objUrl);
            drawBracket(canvas, data, null, mode, resultMap, eliminatedIds, resultGamesById);
          };
          img.src = objUrl;
        })
        .catch(err => {
          console.warn('BracketPrintView: logo load failed, using fallback.', err.message);
          drawBracket(canvas, data, null, mode, resultMap, eliminatedIds, resultGamesById);
        });
    } else {
      drawBracket(canvas, data, null, mode, resultMap, eliminatedIds, resultGamesById);
    }
    } catch(err) { console.error('[DRAW] Error in draw:', err); }
  }, [bracket, games, teamsById, wiring, branding, mode]);

  useEffect(() => {
    console.log('[DRAW] useEffect firing, scheduling draw');
    // Use timeout as primary method — document.fonts.ready can hang in some browsers
    const t = setTimeout(draw, 150);
    // Also try fonts.ready as bonus for crispness, but don't rely on it
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        clearTimeout(t);
        draw();
      }).catch(() => {}); // ignore fonts errors
    }
    return () => clearTimeout(t);
  }, [draw]);

  const handlePrint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html>
<html><head><title>${bracket?.name || 'Bracket'}</title>
<style>*{margin:0;padding:0;}body{background:#fff;}
img{width:100%;height:auto;max-height:100vh;display:block;}
@page{size:11in 8.5in landscape;margin:0.2in;}
@media print{img{width:100%;page-break-inside:avoid;}}
</style></head><body>
<img src="${dataUrl}" onload="window.print();setTimeout(()=>window.close(),500);" />
</body></html>`);
    win.document.close();
  }, [bracket]);

  return (
    <>
      <style>{`
        @keyframes bpvFade{from{opacity:0}to{opacity:1}}
        .bpv-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.82);z-index:1000;
          display:flex;flex-direction:column;align-items:center;padding:16px;gap:10px;
          overflow-y:auto;animation:bpvFade .15s ease;}
        .bpv-toolbar{display:flex;align-items:center;gap:10px;background:#122040;
          border-radius:8px;padding:10px 16px;width:100%;max-width:1060px;flex-shrink:0;}
        .bpv-toolbar span{flex:1;font-family:'Barlow Condensed',sans-serif;font-weight:700;
          font-size:1rem;color:#f0f6ff;letter-spacing:1px;}
        .bpv-print{background:#4B8BBE;color:#fff;border:none;border-radius:6px;
          padding:8px 22px;font-family:'Barlow Condensed',sans-serif;font-weight:700;
          font-size:.95rem;letter-spacing:1px;cursor:pointer;}
        .bpv-close{background:transparent;color:#7a94b8;border:1px solid #2a4470;
          border-radius:6px;padding:8px 14px;font-family:'Barlow Condensed',sans-serif;
          font-weight:700;cursor:pointer;}
        .bpv-hint{font-family:'Barlow Condensed',sans-serif;font-size:.78rem;color:#888;text-align:center;}
        .bpv-canvas{box-shadow:0 4px 24px rgba(0,0,0,.5);max-width:100%;}
      `}</style>
      <div className="bpv-backdrop">
        <div className="bpv-toolbar">
          <span>🖨 {bracket?.name}</span>
          <div style={{ display: 'flex', gap: 4, background: '#0d1f36', borderRadius: 6, padding: 3 }}>
            <button onClick={() => setMode('picks')} style={{
              padding: '5px 14px', borderRadius: 4, border: 'none', cursor: 'pointer',
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: '0.85rem',
              background: mode === 'picks' ? '#4B8BBE' : 'transparent',
              color: mode === 'picks' ? '#fff' : '#7a94b8',
            }}>📋 My Picks</button>
            <button onClick={() => setMode('results')} style={{
              padding: '5px 14px', borderRadius: 4, border: 'none', cursor: 'pointer',
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: '0.85rem',
              background: mode === 'results' ? '#4B8BBE' : 'transparent',
              color: mode === 'results' ? '#fff' : '#7a94b8',
            }}>✓✗ Results</button>
          </div>
          <button className="bpv-print" onClick={handlePrint}>🖨 Print / Save PDF</button>
          <button className="bpv-close" onClick={onClose}>✕ Close</button>
        </div>
        <div className="bpv-hint">
          Opens print dialog · Choose <strong>Landscape</strong> · Save as PDF or print
        </div>
        <canvas ref={canvasRef} width={2054} height={1524} style={{width:1027,height:762}} className="bpv-canvas" />
      </div>
    </>
  );
}
